import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Rate, Tooltip, notification } from 'antd'
import {
    CameraOutlined,
    CheckOutlined,
    CloseOutlined,
    ColumnHeightOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import ReactCountryFlag from 'react-country-flag'

import TranslationNS from '@/utils/Constants/TranslationNS'
import StatusDot from '@/components/common/StatusDot'
import { getRegistrationStatusDisplay } from '@/components/common/StatusDot/index.utils'
import { calculRegistrationState } from '@/middlewares/services/home.services'
import { getCompetitionReviews } from '@/middlewares/services/review.services'
import { EditCompetitionName, updateCompetition } from '@/middlewares/services/competition.services.js'
import { EventServices } from '@/middlewares/services'
import { useOrganizerShell } from '@/components/Organizer/OrganizerShell/OrganizerShellContext'
import { initialsOf } from '@/components/Organizer/OrganizerShell/orgMenu.model'
import { useEventHub } from './EventHubContext'
import EventHubUploadModal from './EventHubUploadModal'
import EventHubPublishCard from './settings/EventHubPublishCard'
import { EVENT_HUB_UPLOAD_KIND } from './eventHub.upload.utils'
import {
    CATEGORY_ICON,
    EVENT_HUB_BANNER_CHIP_ACTIVE_CLASS,
    EVENT_HUB_BANNER_CHIP_CLASS,
    EVENT_HUB_BANNER_CHIP_IDLE_CLASS,
    EVENT_HUB_BANNER_ERROR_CLASS,
    EVENT_HUB_BANNER_HINT_CLASS,
    EVENT_HUB_BANNER_HOVER_OVERLAY_CLASS,
    EVENT_HUB_BANNER_HOVER_REVEAL_CLASS,
    EVENT_HUB_BANNER_ICON_CHIP_CLASS,
    EVENT_HUB_BANNER_POSITION_DEFAULT,
    EVENT_HUB_CANCEL_CONTROL_CLASS,
    EVENT_HUB_CONFIRM_CONTROL_CLASS,
    EVENT_HUB_CONTROL_PAIR_CLASS,
    EVENT_HUB_EDIT_ACCENT_CLASS,
    EVENT_HUB_EDIT_CONTROL_SM_CLASS,
    EVENT_HUB_HERO_CANCEL_CONTROL_CLASS,
    EVENT_HUB_HERO_CONFIRM_CONTROL_CLASS,
    EVENT_HUB_HERO_EDIT_CONTROL_CLASS,
    EVENT_HUB_IMAGE_OVERLAY_CLASS,
    EVENT_HUB_LOGO_HOVER_OVERLAY_CLASS,
    EVENT_HUB_LOGO_HOVER_REVEAL_CLASS,
    EVENT_HUB_PINNED_MENU_TITLE_CLASS,
    EVENT_HUB_TITLE_CLASS,
    EVENT_HUB_TOOLTIP_PROPS,
    countryAlpha2,
    getEventHubBase,
    isBannerWide,
} from './eventHub.utils'
import StarSVG from '@/images/svgs/Star.svg?react'
import EditSVG from '@/images/svgs/Edit.svg?react'
import { DEFAULT_EVENT_BANNER, getEventBannerUrl, hasCustomEventBanner } from '@/utils/eventBanner'
import FallbackImg from '@/components/common/FallbackImg'
import defaultLogo from '@/images/scoring_icon.svg'

/** How long "Image Uploaded !" stays on the banner before it clears itself. */
const BANNER_UPLOADED_NOTICE_MS = 3000

/**
 * Banner upload progress, filling the row to the left of the Edit chip (Figma
 * 3790:25867, mock `.bn-left` / `.bn-prog`: 12px white text, 4px bar at 35% white
 * filling solid white, 1000px at most). Inline rather than behind the app's
 * blocking loader, so the rest of the page stays usable while the file goes up.
 */
function BannerUploadProgress({ percent, label }) {
    return (
        <span className="flex min-w-0 flex-1 items-center gap-4 text-xs text-white">
            <span className="shrink-0">{label}</span>
            <span className="h-1 min-w-0 max-w-[1000px] flex-1 overflow-hidden rounded-sm bg-white/35">
                <span
                    className="block h-full rounded-sm bg-white transition-[width]"
                    style={{ width: `${percent}%` }}
                />
            </span>
        </span>
    )
}

/** Why an upload failed, shown beside the chip (Figma 3790:25700, mock `.bn-err`). */
function UploadWarning({ children }) {
    return (
        <span role="alert" className={`${EVENT_HUB_BANNER_ERROR_CLASS} text-left`}>
            <InfoCircleOutlined className="shrink-0 text-[15px]" />
            {children}
        </span>
    )
}

/**
 * Organizer "Edit" chip (camera + label) opening the shared upload dialog, which
 * validates the file (type, weight, dimensions) before anything is sent. Sits on a
 * dimmed image region, so it is outlined in #dbdadf with no fill (Figma 3790:24908).
 *
 * `warning` (a failed upload) pins the whole row visible so the message survives
 * the pointer leaving the region.
 *
 * `compact` narrows the sides under `nav` so the chip fits inside the 92px logo tile.
 */
/**
 * "Edit" chip over an editable image. `iconOnly` (image already set) folds it into
 * the 32px camera square so it no longer covers the photo; the label survives as
 * aria-label. `compact` tightens the labeled chip's sides on the mobile logo tile.
 */
function EditChip({ tooltip, warning, onClick, className = '', revealClass = '', compact = false, iconOnly = false }) {
    const { t } = useTranslation(TranslationNS.EVENT_PAGE)

    const button = iconOnly ? (
        <button
            type="button"
            onClick={onClick}
            aria-label={t('Edit')}
            className={`${EVENT_HUB_BANNER_ICON_CHIP_CLASS} ${EVENT_HUB_BANNER_CHIP_IDLE_CLASS}`}
        >
            <CameraOutlined />
        </button>
    ) : (
        <button
            type="button"
            onClick={onClick}
            className={`${EVENT_HUB_BANNER_CHIP_CLASS} ${EVENT_HUB_BANNER_CHIP_IDLE_CLASS} ${
                compact ? 'max-nav:px-2.5' : ''
            }`}
        >
            <CameraOutlined className="text-[13px]" />
            {t('Edit')}
        </button>
    )

    return (
        <div className={`flex items-center gap-2.5 ${warning ? '' : revealClass} ${className}`}>
            {warning && <UploadWarning>{warning}</UploadWarning>}
            {tooltip ? (
                <Tooltip title={tooltip} {...EVENT_HUB_TOOLTIP_PROPS}>
                    {button}
                </Tooltip>
            ) : (
                button
            )}
        </div>
    )
}

/**
 * Organizer inline title edit (mock `.bn-title-wrap`): hovering the banner draws a
 * dashed frame 5px around the name with a 23px pencil 3px inside its corner
 * (Figma 3790:24873 / Frame 138); editing swaps in a textarea with the same
 * metrics and ✗ ✓ in the pencil's place (Figma 3790:25236).
 *
 * `light` is the desktop overlay on the photo (translucent white tools); the
 * mobile copy under the image uses the page's blue edit controls instead.
 */
function EditableTitle({ name, light }) {
    const { t } = useTranslation(TranslationNS.EVENT_PAGE)
    const { competition, setCompetition, setIsLoading } = useEventHub()
    const shell = useOrganizerShell()
    const [isEditing, setIsEditing] = useState(false)
    const [draft, setDraft] = useState(name)

    // Same metrics as the public h1: the organizer sees the title as athletes do.
    const titleClass = `${EVENT_HUB_TITLE_CLASS} ${EVENT_HUB_PINNED_MENU_TITLE_CLASS} ${
        light ? 'text-[#272727] nav:text-white' : 'text-[#272727]'
    }`
    // 5px frame around the text, drawn on the wrapper so the h1 keeps its own box.
    const frameClass = '-m-1.25 rounded-[5px] p-1.25'
    const toolsPositionClass = 'absolute right-0.75 top-0.75'
    const penClass = light
        ? `${EVENT_HUB_HERO_EDIT_CONTROL_CLASS} invisible group-hover:visible nav:group-hover/banner:visible`
        : `${EVENT_HUB_EDIT_ACCENT_CLASS} bg-white hover:bg-[#1672ec] hover:text-white`
    const cancelClass = light ? EVENT_HUB_HERO_CANCEL_CONTROL_CLASS : EVENT_HUB_CANCEL_CONTROL_CLASS
    const confirmClass = light ? EVENT_HUB_HERO_CONFIRM_CONTROL_CLASS : EVENT_HUB_CONFIRM_CONTROL_CLASS

    const startEditing = () => {
        setDraft(name)
        setIsEditing(true)
    }

    const confirm = async () => {
        const newName = draft.trim()
        if (!newName || newName === name) {
            setIsEditing(false)
            return
        }
        setIsLoading(true)
        try {
            await EditCompetitionName(competition._id, newName)
            setCompetition((previous) => ({ ...previous, name: newName }))
            shell?.setIdentity({ name: newName })
            notification.success({ message: t('Competition name updated successfully !') })
        } catch (error) {
            console.error('/EditableTitle confirm - error', error)
            notification.error({ message: t('Oops, an error occurred !') })
        }
        setIsLoading(false)
        setIsEditing(false)
    }

    if (!isEditing) {
        return (
            <div
                onClick={startEditing}
                // The mobile copy has no hover, so its pencil is always visible: it is anchored on the
                // text column's top right, beside the labels (which keep clear of it), so the name keeps
                // the column's full width — wrapped and clamped as athletes see it.
                className={`group cursor-text outline-1 outline-dashed outline-transparent transition-[outline-color] ${frameClass} ${
                    light
                        ? 'relative group-hover:outline-white/55 nav:group-hover/banner:outline-white/55'
                        : 'group-hover:outline-[#1672ec]'
                }`}
            >
                <h1 className={titleClass}>{name}</h1>
                <button
                    type="button"
                    aria-label={t('Edit')}
                    onClick={startEditing}
                    className={`${EVENT_HUB_EDIT_CONTROL_SM_CLASS} ${toolsPositionClass} ${penClass}`}
                >
                    <EditSVG className="size-3" />
                </button>
            </div>
        )
    }

    // The field keeps the h1 metrics inside a dashed hairline box over the darkened
    // banner (mock `.bn-title-in`: white at 6%, type at 70%); the mobile copy sits
    // on a field-grey box with the blue edit border.
    return (
        <div
            className={`relative min-h-14 border border-dashed nav:min-h-[90px] ${frameClass} pr-[60px] ${
                light ? 'border-white/60 bg-white/6' : 'border-[#1672ec] bg-[#f7f7f9]'
            }`}
        >
            <textarea
                autoFocus
                rows={2}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault()
                        confirm()
                    }
                    if (event.key === 'Escape') setIsEditing(false)
                }}
                // mobile: three lines tall — the cancel / confirm pair narrows the field, so a two-line name
                // would scroll inside two rows
                className={`font-display block w-full resize-none border-0 bg-transparent p-0 text-[20px] font-bold leading-[1.14] outline-none max-nav:h-[3lh] nav:text-[40px] nav:leading-none ${
                    light ? 'text-[#5b5f6e] nav:text-white/70' : 'text-[#5b5f6e]'
                }`}
            />
            <div className={`${EVENT_HUB_CONTROL_PAIR_CLASS} ${toolsPositionClass}`}>
                <button
                    type="button"
                    aria-label={t('Cancel')}
                    onClick={() => setIsEditing(false)}
                    className={`${EVENT_HUB_EDIT_CONTROL_SM_CLASS} ${cancelClass}`}
                >
                    <CloseOutlined />
                </button>
                <button
                    type="button"
                    aria-label={t('Save')}
                    onClick={confirm}
                    className={`${EVENT_HUB_EDIT_CONTROL_SM_CLASS} ${confirmClass}`}
                >
                    <CheckOutlined />
                </button>
            </div>
        </div>
    )
}

/** Event identity: logo + (category · country · status) + title + tags + rating. */
function Identity({ showStatus, align, competition, presentation, status, rating, reviewsHref, canEdit, onEditLogo }) {
    const { t } = useTranslation(TranslationNS.EVENT_PAGE)
    const alpha2 = countryAlpha2(presentation?.country)
    const tags = (presentation?.tags || []).filter((tag) => tag !== 'inside' && tag !== 'online')

    const CategoryIcon = CATEGORY_ICON[presentation.category]

    // Mock `.banner`: logo | 18px | text column (13px between labels, title and chips);
    // mobile frame (event.css ≤760): logo 92 radius 10, 10px gutter, 6px between rows.
    return (
        <div className={`flex gap-2.5 nav:gap-[18px] ${align === 'end' ? 'items-end' : 'items-start'}`}>
            {(competition?.iconLink || canEdit) && (
                <div className="group/logo relative shrink-0 overflow-hidden rounded-[10px] nav:rounded-[20px]">
                    {competition?.iconLink ? (
                        <FallbackImg
                            src={competition.iconLink}
                            fallback={defaultLogo}
                            alt=""
                            className="size-[92px] nav:size-[154px] rounded-[10px] nav:rounded-[20px] border border-[#dbdadf] bg-white object-contain"
                        />
                    ) : (
                        // Mock blank event (`body.ev-blank .banner-logo`): no logo → the initials on ink.
                        // Under `nav` the Edit chip always takes the centre, so the initials stay hidden there.
                        <div className="grid size-[92px] place-items-center nav:size-[154px] rounded-[10px] nav:rounded-[20px] border border-[#dbdadf] bg-[#272727]">
                            <span
                                aria-hidden
                                className="font-display text-[48px] font-extrabold leading-none tracking-[.02em] text-white max-nav:hidden"
                            >
                                {initialsOf(competition?.name)}
                            </span>
                        </div>
                    )}
                    {canEdit && (
                        <>
                            <div className={EVENT_HUB_LOGO_HOVER_OVERLAY_CLASS} />
                            {/* No inline warning: the tile is too narrow for one, and a
                                failed upload already raises a notification. With a logo the
                                chip shrinks to the camera square in the bottom-right corner;
                                the blank tile keeps the labeled chip centred. */}
                            <EditChip
                                tooltip={t('JPG or PNG, max 2 MB')}
                                onClick={onEditLogo}
                                revealClass={EVENT_HUB_LOGO_HOVER_REVEAL_CLASS}
                                iconOnly={Boolean(competition?.iconLink)}
                                className={
                                    competition?.iconLink
                                        ? 'absolute bottom-2 right-2'
                                        : 'absolute inset-0 justify-center'
                                }
                                compact
                            />
                        </>
                    )}
                </div>
            )}
            {/* relative: the mobile title pencil (EditableTitle) is anchored on this column's top right */}
            <div className="relative flex min-w-0 flex-col gap-1.5 nav:gap-[13px]">
                {/* Mock `.banner-labels`: Comp 14px, gap 19; category Regular, country and status Light.
                    Mobile: category becomes a 21px outlined chip (Inter 10), gap 10; for an organizer the row
                    keeps clear of the title pencil. */}
                <div
                    className={`flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm text-[#272727] nav:gap-x-[19px] nav:gap-y-2 nav:text-white ${
                        canEdit ? 'max-nav:pr-[30px]' : ''
                    }`}
                >
                    <div className="inline-flex h-[21px] w-fit min-w-0 items-center gap-[5px] rounded-[5px] border border-[#272727] px-[5px] text-[10px] font-normal [font-family:var(--font-body)] nav:h-auto nav:gap-1.5 nav:border-0 nav:px-0 nav:text-sm nav:[font-family:var(--font-meta)] nav:[font-stretch:75%]">
                        {CategoryIcon && <CategoryIcon className="h-3 w-2.5 flex-none nav:h-[21px] nav:w-[17px]" />}
                        <span>{presentation.category}</span>
                    </div>
                    {presentation?.country && (
                        <span className="inline-flex items-center gap-1.5 nav:font-light">
                            {alpha2 && (
                                <ReactCountryFlag countryCode={alpha2} svg style={{ width: '21px', height: '14px' }} />
                            )}
                            {presentation.country}
                        </span>
                    )}
                    {showStatus && status && (
                        <span className="inline-flex items-center gap-1.5 nav:font-light">
                            <StatusDot color={status.color} className="size-[21px]" />
                            {status.label}
                        </span>
                    )}
                    {canEdit && (
                        <Tooltip
                            title={t('These details update automatically based on your event settings.')}
                            {...EVENT_HUB_TOOLTIP_PROPS}
                        >
                            <InfoCircleOutlined className="cursor-help text-[15px]" />
                        </Tooltip>
                    )}
                </div>

                {canEdit ? (
                    <EditableTitle name={competition?.name} light={align === 'end'} />
                ) : (
                    /* Mock `.banner-title`: SFT display 40/40 clamped to two lines; mobile 20/1.14. */
                    <h1
                        className={`${EVENT_HUB_TITLE_CLASS} text-[#272727] nav:text-white ${EVENT_HUB_PINNED_MENU_TITLE_CLASS}`}
                    >
                        {competition?.name}
                    </h1>
                )}

                {/* Mock `.banner-bottom`: chips (gap 10) then, 11px later, stars + reviews link.
                    Mobile puts the rating ABOVE the chips (5px under the title, 10px above the chips). */}
                <div className="flex max-nav:flex-col-reverse max-nav:items-start max-nav:gap-2.5 nav:flex-wrap nav:items-center nav:gap-y-2">
                    <span className="inline-flex items-center gap-2.5">
                        {tags.map((tag) => (
                            <span
                                key={tag}
                                className="inline-flex h-[21px] items-center justify-center whitespace-nowrap rounded-[5px] border border-[#272727] px-[5px] text-[10px] text-[#272727] [font-family:var(--font-body)] nav:h-auto nav:border-[#dbdadf] nav:px-[9px] nav:py-1 nav:text-sm nav:leading-[17px] nav:text-[#dbdadf] nav:[font-family:var(--font-meta)] nav:[font-stretch:75%]"
                            >
                                #{tag.charAt(0).toUpperCase()}
                                {tag.slice(1).toLowerCase()}
                            </span>
                        ))}
                    </span>
                    {rating?.count > 0 && (
                        <span className="inline-flex h-[14px] items-center gap-1.5 nav:h-auto nav:ml-[11px]">
                            <Rate
                                disabled
                                allowHalf
                                value={rating.avg}
                                style={{ fontSize: 14 }}
                                character={() => <StarSVG />}
                            />
                            {/* mock `.banner-reviews`: the count links to the Reviews tab */}
                            <Link
                                to={reviewsHref}
                                className="font-meta text-xs underline text-[#272727] hover:text-[#272727] nav:text-[#dbdadf] nav:hover:text-white"
                            >
                                {t('+{{count}} Reviews', { count: rating.count })}
                            </Link>
                        </span>
                    )}
                </div>
            </div>

            {/* Reserves the right column (≥ cols, where the side panel overlaps the banner) so it never
                hides the title. Width tracks --eh-sidebar-w — the same var that sizes the panel's grid column. */}
            <div aria-hidden className="hidden shrink-0 cols:block cols:w-(--eh-sidebar-w)" />
        </div>
    )
}

/**
 * Shared hero. Rendered once by EventHub.jsx (never per tab).
 * Desktop: full-bleed banner with the identity overlaid bottom-left.
 * Mobile: banner with a status pill, identity stacked below the image.
 * Organizer (canEdit) mode adds banner/logo Edit chips and inline title edit; once
 * an image is set its chips fold into icon-only squares (see EditChip).
 */
export default function EventHubHero() {
    const { competition, presentation, canEdit, eventIdentifier, setCompetition, setIsLoading } = useEventHub()
    const { t } = useTranslation(TranslationNS.HOME_PAGE)
    const { t: tEvent } = useTranslation(TranslationNS.EVENT_PAGE)
    const { t: tMonths } = useTranslation(TranslationNS.MONTHS)
    const [rating, setRating] = useState()
    // Banner image upload: null when idle, `{ percent }` in flight (Figma 3790:25760),
    // then `{ done: true }` for the short confirmation afterwards (Figma 3790:25872),
    // or `{ error: true }` when the request failed (Figma 3790:25700).
    const [bannerStatus, setBannerStatus] = useState(null)
    // Which image the upload dialog is open for: 'banner' | 'logo' | null.
    const [uploadKind, setUploadKind] = useState(null)
    // Vertical focal point being dragged, as a percentage (Figma 3790:25984). null when
    // reposition mode is off, in which case the saved value is shown.
    const [draftPosition, setDraftPosition] = useState(null)
    const dragRef = useRef(null)
    const shell = useOrganizerShell()
    const { pathname } = useLocation()
    const base = getEventHubBase(pathname, eventIdentifier)

    // Fit by ratio (mock `fitBanner`): decided on the image's natural size against its frame, replayed
    // on load, resize and every route (the banner may be hidden at load: then nothing is decided).
    const frameRef = useRef(null)
    const imgRef = useRef(null)
    const [ratioWide, setRatioWide] = useState(false)
    const fitBanner = useCallback(() => {
        const img = imgRef.current
        const frame = frameRef.current
        if (!img || !frame) return
        const rect = frame.getBoundingClientRect()
        if (!rect.height) return
        setRatioWide(isBannerWide(img.naturalWidth, img.naturalHeight, rect.width, rect.height))
    }, [])
    // The rule is for the old site's typographic bands: the Scoring.fit default banner is a 3:1 photo,
    // "wide" against every desktop frame, and always fills its frame in `cover`.
    const hasCustomBanner = hasCustomEventBanner(competition)
    const fitWide = ratioWide && hasCustomBanner
    useEffect(() => {
        const frame = frameRef.current
        if (!frame || typeof ResizeObserver === 'undefined') return undefined
        const observer = new ResizeObserver(() => fitBanner())
        observer.observe(frame)
        return () => observer.disconnect()
    }, [fitBanner])
    useEffect(() => {
        const raf = requestAnimationFrame(fitBanner)
        return () => cancelAnimationFrame(raf)
    }, [pathname, competition?.illustrationLink, fitBanner])

    const savedPosition = competition?.illustrationPosition ?? EVENT_HUB_BANNER_POSITION_DEFAULT
    const isRepositioning = draftPosition !== null
    const bannerPosition = isRepositioning ? draftPosition : savedPosition

    const status = getRegistrationStatusDisplay(
        calculRegistrationState(competition?._ticketing, competition?.divisions, competition?.total_participants),
        t,
        tMonths,
        competition?.timezone,
    )

    useEffect(() => {
        let active = true
        if (!competition?._id) return undefined
        getCompetitionReviews(competition._id)
            .then((res) => {
                if (active && typeof res?.data?.count === 'number') {
                    setRating({ avg: res.data.avg, count: res.data.count })
                }
            })
            .catch(() => {})
        return () => {
            active = false
        }
    }, [competition?._id])

    // "Image Uploaded !" is a confirmation, not a resting state — drop it after a beat
    // so the banner goes back to plain hover behaviour.
    useEffect(() => {
        if (!bannerStatus?.done && !bannerStatus?.error) return undefined
        const timer = setTimeout(() => setBannerStatus(null), BANNER_UPLOADED_NOTICE_MS)
        return () => clearTimeout(timer)
    }, [bannerStatus])

    // "Reposition" is a toggle (the design shows no confirm control): the first click
    // arms the drag, the second leaves the mode and persists the focal point.
    const toggleReposition = async () => {
        if (!isRepositioning) {
            setDraftPosition(savedPosition)
            return
        }

        const position = Math.round(draftPosition)
        // Commit the saved value before dropping the draft, otherwise the banner
        // renders one frame at the old focal point while the request is in flight.
        setCompetition((previous) => ({ ...previous, illustrationPosition: position }))
        setDraftPosition(null)
        if (position === savedPosition) {
            return
        }

        setIsLoading(true)
        try {
            await updateCompetition(competition._id, { illustrationPosition: position })
            notification.success({ message: tEvent('Saved successfully') })
        } catch (error) {
            console.error('/EventHubHero toggleReposition - error', error)
            setCompetition((previous) => ({ ...previous, illustrationPosition: savedPosition }))
            notification.error({ message: tEvent('Oops, an error occurred !') })
        }
        setIsLoading(false)
    }

    // Dragging down pans the visible window towards the top of the image, so the
    // focal point moves the opposite way to the pointer.
    const startDrag = (event) => {
        event.currentTarget.setPointerCapture(event.pointerId)
        dragRef.current = {
            startY: event.clientY,
            startPosition: draftPosition,
            height: event.currentTarget.clientHeight || 1,
        }
    }

    const onDrag = (event) => {
        const drag = dragRef.current
        if (!drag) return
        const delta = ((event.clientY - drag.startY) / drag.height) * 100
        setDraftPosition(Math.min(100, Math.max(0, drag.startPosition - delta)))
    }

    const endDrag = () => {
        dragRef.current = null
    }

    // Same upload flow as the old EventPage: target 'background' → illustrationLink,
    // 'logo' → iconLink. The ?k= suffix busts the browser cache when the S3 key is reused.
    //
    // The banner reports progress inline on the hero instead of raising the app's
    // blocking loader (Figma 3790:25760); the logo tile has no room for a bar, so
    // it still uses the loader.
    const uploadImage = async (target, file) => {
        const inline = target === 'background'
        if (inline) {
            setBannerStatus({ percent: 0 })
        } else {
            setIsLoading(true)
        }
        try {
            const data = new FormData()
            data.append('file', file)
            const result = await EventServices.UploadImageAndUpdateCompetition(
                competition._id,
                target,
                data,
                inline
                    ? {
                          onUploadProgress: (event) =>
                              setBannerStatus({
                                  percent: event.total ? Math.round((event.loaded / event.total) * 100) : 0,
                              }),
                      }
                    : {},
            )
            if (result.data.url) {
                const url = `${result.data.url}${result.data.url.includes('?') ? '&' : '?'}k=${Date.now()}`
                setCompetition((previous) => ({
                    ...previous,
                    [target === 'background' ? 'illustrationLink' : 'iconLink']: url,
                }))
                if (!inline) shell?.setIdentity({ iconLink: url })
                // The banner confirms on itself; only the logo needs the toast.
                if (inline) {
                    setBannerStatus({ done: true })
                } else {
                    notification.success({ message: tEvent('Image uploaded saved successfully') })
                }
            } else if (inline) {
                setBannerStatus(null)
            }
        } catch (error) {
            console.error('/EventHubHero uploadImage - error', error)
            notification.error({ message: tEvent('Failed to upload image') })
            if (inline) {
                setBannerStatus({ error: true })
            }
        } finally {
            if (!inline) {
                setIsLoading(false)
            }
        }
    }

    const identityProps = {
        competition,
        presentation,
        rating,
        reviewsHref: `${base}/reviews`,
        canEdit,
        onEditLogo: () => setUploadKind(EVENT_HUB_UPLOAD_KIND.LOGO),
    }

    // Not awaited: the dialog closes at once and the hero reports progress itself
    // (banner inline, logo behind the app loader).
    const handleUpload = (files) => {
        uploadImage(uploadKind === EVENT_HUB_UPLOAD_KIND.BANNER ? 'background' : 'logo', files[0])
    }

    return (
        <header className="px-(--x004) pt-4 nav:px-(--x006)" data-tour="hub-hero">
            {/* organizer: the « Event page » publish pill, right-aligned 12 px above the banner */}
            {canEdit && (
                <div className="mb-3 flex justify-end">
                    <EventHubPublishCard />
                </div>
            )}
            {/* The banner keeps the mock's 1220 × 505 silhouette at every width (mock `.banner{aspect-ratio}`,
                William 12 Oct 2026). `overflow-clip` rounds the corners without dropping the automatic
                minimum height: should the in-flow identity outgrow the ratio, the banner grows instead of
                cutting its top. */}
            <div
                ref={frameRef}
                className="group/banner relative aspect-[1220/505] w-full overflow-clip rounded-[10px] border border-[#dbdadf] bg-gray-200 nav:flex nav:flex-col nav:justify-end nav:rounded-[20px]"
            >
                {/* fit-wide (mock `.banner-bgw`): a blurred, enlarged copy of the image under the whole image;
                    clipped by its own box so the blur never bleeds past the rounded frame */}
                {fitWide && (
                    <div aria-hidden className="absolute inset-0 overflow-hidden">
                        <img
                            src={getEventBannerUrl(competition)}
                            alt=""
                            className="size-full scale-150 object-cover blur-[24px] saturate-[1.15] nav:scale-[1.12]"
                        />
                    </div>
                )}
                {/* custom illustration or the Scoring.fit default banner, also when the link is dead */}
                <img
                    ref={imgRef}
                    src={getEventBannerUrl(competition)}
                    onError={(e) => {
                        e.currentTarget.onerror = null
                        e.currentTarget.src = DEFAULT_EVENT_BANNER
                    }}
                    alt=""
                    onLoad={fitBanner}
                    className={`absolute inset-0 size-full ${
                        fitWide ? 'object-contain object-center' : 'object-cover'
                    }`}
                    style={fitWide ? undefined : { objectPosition: `50% ${bannerPosition}%` }}
                />

                {/* blurred, masked overlay for legibility — same treatment as the home EventCard */}
                <div className="pointer-events-none absolute inset-0 bg-(image:--bg-event-overlay) backdrop-blur-[2.7px] mask-(--mask-event-overlay2) nav:mask-(--mask-event-overlay)" />

                {canEdit && (
                    <>
                        {/* An upload, its confirmation, and reposition mode all hold the dim
                            open instead of waiting on hover. */}
                        <div
                            className={
                                bannerStatus || isRepositioning
                                    ? EVENT_HUB_IMAGE_OVERLAY_CLASS
                                    : EVENT_HUB_BANNER_HOVER_OVERLAY_CLASS
                            }
                        />

                        {/* Drag surface, only present while repositioning — it deliberately
                            covers the identity so the whole banner is grabbable. */}
                        {isRepositioning && (
                            <div
                                className="absolute inset-0 z-10 cursor-ns-resize touch-none"
                                onPointerDown={startDrag}
                                onPointerMove={onDrag}
                                onPointerUp={endDrag}
                                onPointerCancel={endDrag}
                            />
                        )}

                        {/* Only spans the banner while the left slot is in use, so the row
                            never sits over the image as an invisible click target. */}
                        <div
                            className={`absolute z-20 flex items-center gap-2.5 max-nav:top-2.5 max-nav:flex-wrap max-nav:justify-end nav:top-4.75 ${
                                bannerStatus || isRepositioning
                                    ? 'max-nav:inset-x-2.5 nav:inset-x-4.75'
                                    : 'max-nav:right-2.5 nav:right-4.75'
                            }`}
                        >
                            {isRepositioning ? (
                                <span className={`${EVENT_HUB_BANNER_HINT_CLASS} ml-auto`}>
                                    {tEvent('Drag up or down to reposition')}
                                </span>
                            ) : bannerStatus?.done ? (
                                <span className="text-xs text-white">{tEvent('Image Uploaded !')}</span>
                            ) : (
                                bannerStatus &&
                                !bannerStatus.error && (
                                    <BannerUploadProgress
                                        percent={bannerStatus.percent}
                                        label={tEvent('Uploading...')}
                                    />
                                )
                            )}

                            {/* Custom banner: icon-only squares (Reposition = vertical arrows, Edit =
                                camera) so the pair stops eating the photo; the default banner keeps the
                                labeled Edit chip. */}
                            <div className={`flex items-center gap-2.5 ${isRepositioning ? '' : 'ml-auto'}`}>
                                {hasCustomBanner && !fitWide && (
                                    <Tooltip title={tEvent('Reposition')} {...EVENT_HUB_TOOLTIP_PROPS}>
                                        <button
                                            type="button"
                                            aria-pressed={isRepositioning}
                                            aria-label={tEvent('Reposition')}
                                            onClick={toggleReposition}
                                            className={`${EVENT_HUB_BANNER_ICON_CHIP_CLASS} ${
                                                isRepositioning
                                                    ? EVENT_HUB_BANNER_CHIP_ACTIVE_CLASS
                                                    : EVENT_HUB_BANNER_CHIP_IDLE_CLASS
                                            } ${
                                                bannerStatus || isRepositioning
                                                    ? ''
                                                    : EVENT_HUB_BANNER_HOVER_REVEAL_CLASS
                                            }`}
                                        >
                                            <ColumnHeightOutlined />
                                        </button>
                                    </Tooltip>
                                )}
                                <EditChip
                                    iconOnly={hasCustomBanner}
                                    tooltip={tEvent(
                                        '1920 × 1080 px recommended (min. 1920 × 640), JPG or PNG, max 5 MB',
                                    )}
                                    warning={bannerStatus?.error ? tEvent('Failed to upload image') : ''}
                                    onClick={() => {
                                        setBannerStatus(null)
                                        setUploadKind(EVENT_HUB_UPLOAD_KIND.BANNER)
                                    }}
                                    revealClass={
                                        bannerStatus || isRepositioning ? '' : EVENT_HUB_BANNER_HOVER_REVEAL_CLASS
                                    }
                                />
                            </div>
                        </div>
                    </>
                )}

                {/* mobile: status on the photo strip, 10px from the right and 11px from the bottom (event.css ≤760) */}
                <span className="absolute bottom-[11px] right-2.5 inline-flex items-center gap-[5px] text-sm text-white nav:hidden">
                    <StatusDot color={status.color} className="size-[21px]" />
                    {status.label}
                </span>

                {/* desktop: identity bottom-left, in flow (padding 16 34 34 of the mock `.banner`) — faded to
                    40% while the banner is being repositioned or has just been replaced (mock `.cover-uploaded`). */}
                <div
                    className={`relative hidden px-[34px] pb-[34px] pt-4 transition-opacity nav:block ${
                        isRepositioning || bannerStatus?.done ? 'opacity-40' : ''
                    }`}
                >
                    <Identity showStatus align="end" status={status} {...identityProps} />
                </div>
            </div>

            {/* mobile: identity 18px below the photo strip (event.css ≤760) */}
            <div className="mt-[18px] nav:hidden">
                <Identity showStatus={false} align="start" status={status} {...identityProps} />
            </div>

            {uploadKind && (
                <EventHubUploadModal kind={uploadKind} onClose={() => setUploadKind(null)} onUpload={handleUpload} />
            )}
        </header>
    )
}
